import { clsx } from 'clsx';
import i18n from 'helpers/i18n';
import messages from './messages';
import { RadioGroup, RadioGroupItem } from '@radix-ui/react-radio-group';

interface ScoreRadioGroupItemProps {
  value: string;
  suggested: boolean;
  children: React.ReactNode;
  isInformal?: boolean;
}

function ScoreRadioGroupItem({
  value,
  suggested,
  children,
  isInformal
}: ScoreRadioGroupItemProps) {
  const classes = clsx(
    'transition-colors',
    // base state
    'bg-ds-white hover:bg-ds-surface-hover',
    // suggested state
    'data-[suggested=true]:bg-scale-light-green data-[suggested=true]:ring-4 data-[suggested=true]:ring-scale-mint-green',
    // checked state
    'data-[state=checked]:bg-scale-green data-[state=checked]:text-white',
    // formal vs informal design
    isInformal
      ? 'px-4 py-1 text-center rounded-xl capitalize rounded-xl'
      : 'w-8 h-8 flex items-center justify-center rounded-full font-bold shadow-md'
  );

  return (
    <RadioGroupItem
      value={value}
      className={classes}
      data-suggested={suggested}
    >
      {children}
    </RadioGroupItem>
  );
}

const SCORES = [1, 2, 3, 4, 5, 6, 7];

const INFORMAL_OPTIONS = [
  { value: 1, label: i18n.ft(messages.low), scores: [1, 2] },
  { value: 4, label: i18n.ft(messages.mid), scores: [3, 4, 5] },
  { value: 7, label: i18n.ft(messages.high), scores: [6, 7] }
];

interface DimensionScoreInputProps {
  /** Dimension score value. */
  value: number | null;

  /** Callback fired then the dimension score changes. */
  onChange: (value: number | null) => void;

  /** Array of suggested dimension scores. */
  suggestedScores: number[];

  /** Whether the dimension score input should use the informal design. */
  isInformal: boolean;

  /** The ID of the element that labels the dimension score input. */
  ariaLabelledby?: string;
}

export function DimensionScoreInput({
  value,
  onChange,
  suggestedScores,
  ariaLabelledby,
  isInformal
}: DimensionScoreInputProps) {
  function handleValueChange(value: string) {
    onChange(parseInt(value));
  }

  return (
    <RadioGroup
      className={isInformal ? 'grid grid-cols-4 gap-3 w-fit' : 'flex gap-3'}
      aria-labelledby={ariaLabelledby}
      onValueChange={handleValueChange}
      value={value !== null && value !== undefined ? value.toString() : ''}
    >
      {isInformal
        ? INFORMAL_OPTIONS.map(option => (
            <ScoreRadioGroupItem
              key={option.value}
              isInformal
              value={option.value.toString()}
              suggested={
                !value &&
                option.scores.some(score => suggestedScores.includes(score))
              }
            >
              {option.label}
            </ScoreRadioGroupItem>
          ))
        : SCORES.map(score => (
            <ScoreRadioGroupItem
              key={score}
              value={score.toString()}
              suggested={!value && suggestedScores.includes(score)}
            >
              {score}
            </ScoreRadioGroupItem>
          ))}
    </RadioGroup>
  );
}
